import { Dayjs } from "dayjs";
import { CardSiteDataModel } from "./CardSiteDataModel";
import PinRequired from "../Components/Enums/PinRequired";
import Processor from "../Components/Enums/Processor";

export interface GCInfoModel {
    // Gift Card Information
    giftCardNumber: string;
    giftCardPin: string | null;
    issuerName: string;
    brandId: string;
    brandName: string;
    cashOutAmount: string;
    cardPurchasedDate: Dayjs | null;
    beginningCardBalance: string;
    noPinChecked: boolean;
}

export interface GCInfoErrors {
    giftCardNumberError: boolean;
    giftCardPinError: boolean;
    issuerNameError: boolean;
    brandNameError: boolean;
    cashOutAmountError: boolean;
    cardPurchasedDateError: boolean;
    beginningCardBalanceError: boolean;
    errors: string[];
}

export const validateGCInfo = (model: GCInfoModel, cardSite: CardSiteDataModel | null): GCInfoErrors => {
    let errors : GCInfoErrors = {
        giftCardNumberError: false,
        giftCardPinError: false,
        issuerNameError: false,
        brandNameError: false,
        cashOutAmountError: false,
        cardPurchasedDateError: false,
        beginningCardBalanceError: false,
        errors: []
    }
    // Validate Gift Card Number. The input filter strips formatting; the API owns format and length.
    if (model.giftCardNumber === '') {
        errors.giftCardNumberError = true;
        errors.errors.push('Gift Card Number is required');
    }

    // Validate Gift Card Pin
    if ((cardSite?.pinRequired === PinRequired.REQUIRED ||
        (cardSite?.pinRequired === PinRequired.CARDHOLDER &&
        !model.noPinChecked)) &&
        (model.giftCardPin === '' || model.giftCardPin === null)) {
        errors.giftCardPinError = true;
        errors.errors.push('Gift Card Pin is required');
    }

    // Validate Issuer Name
    if (model.issuerName === '') {
        errors.issuerNameError = true;
        errors.errors.push('Issuer Name is required');
    } 

    // Validate Brand Name
    if (model.brandName === '') {
        errors.brandNameError = true;
        errors.errors.push('Brand Name is required');
    }

    // Validate Cash Out Amount
    if (!Number.isFinite(parseFloat(model.cashOutAmount)) || parseFloat(model.cashOutAmount) <= 0) {
        errors.cashOutAmountError = true;
        errors.errors.push('Remaining Balance is required');
    }

    // The Manual-only fields are gated on the card site's processor, the same source that
    // decides whether GCInfoCard renders them. Casing is load-bearing: the canonical value is
    // documented in the cashout-admin repo at api/CashOutAdminAPI/Domain/Processors.cs.
    const isManual = cardSite?.processorCode === Processor.Manual;

    // Validate Card Purchase Date
    if (isManual && model.cardPurchasedDate === null) {
        errors.cardPurchasedDateError = true;
        errors.errors.push('Card Purchase Date is required');
    }

    // Validate Beginning Card Balance
    if (isManual &&
        (!Number.isFinite(parseFloat(model.beginningCardBalance)) || parseFloat(model.beginningCardBalance) <= 0)) {
        errors.beginningCardBalanceError = true;
        errors.errors.push('Beginning Card Balance is required');
    }
    return errors;
}

export const defaultGCInfoModel = (issuerName: string) : GCInfoModel => {
    return {
        // Gift Card Information
        giftCardNumber: '',
        giftCardPin: '',
        issuerName: issuerName,
        brandId: '',
        brandName: '',
        cashOutAmount: '',
        cardPurchasedDate: null,
        beginningCardBalance: '',
        noPinChecked: false
    }
}
